import { Message } from '../chat/message';
import { LLMAPI, IAPIKey, ApiError, Role, ISaveChat, IChat, IChatInfo, IModel, INewModel, ITraining, ITrainingChunk, IState, IDeleteModelResponse, IBilling, ICheckoutSession, IPaymentStatus, ChatError, TokenPrices } from './llmapi';

export class TigerAPI implements LLMAPI {
    url: string;

    constructor(url: string) {
        this.url = url;
    }

    async get(urlService : string): Promise<any> 
    {
        const response = await fetch(this.url + urlService, {
            method: 'GET',
            credentials: 'same-origin'
        });

        if (response.ok) {
            const text = await response.text();
            if (!text) {
                throw new Error('Empty response from API');
            }

            return JSON.parse(text);
        }
        else if (response.status === 400) {
            const { code, description } = await response.json();

            throw new ApiError(response.status, code, description);
        }
        else {
            throw new Error(`${response.status} ${response.statusText || 'Unknown Status'}`);
        }
    }

    async post(urlService : string, body : object) : Promise<any>
    {
        const response = await fetch(`${this.url}${urlService}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify(body)
        });

        if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const text = await response.text();
                if (text.length > 0) {
                    return JSON.parse(text);
                }
            }
            return;
        }
        else if (response.status === 400) {
            const { code, description } = await response.json();

            throw new ApiError(response.status, code, description);
        }
        else {
            throw new Error(`${response.status} ${response.statusText || 'Unknown Status'}`);
        }
    }

    async changePassword?(email : string, password : string, token : string, altcha : string) : Promise<void>
    {
        const body = {
            email,
            password,
            token,
            altcha
        };

        return await this.post('v1/changepassword', body);
    }

    async createAccount(email : string, password : string, altcha : string) : Promise<void> 
    {
        const body = {
            email    : email,
            password : password,
            altcha   : altcha
        };

        return await this.post('v1/createaccount', body);
    }

    async getCheckout(amount: number): Promise<ICheckoutSession>
    {
        let body = { type: 0, amount: amount};

        return await this.post('v1/checkout', body);
    }

    async getPaymentStatus(checkoutId: string): Promise<IPaymentStatus> {
        try {
            const getPaymentStatusUrl = new URL('v1/payment-status', this.url);
            getPaymentStatusUrl.searchParams.append('checkoutId', checkoutId);

            const response = await fetch(getPaymentStatusUrl, {
                method: 'GET',
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            let result = await response.json();
            return result;
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response.');
        }
    }

    async getCredits(): Promise<number> {
        try {
            const response = await fetch(this.url + 'v1/credits', {
                method: 'GET',
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const text = await response.text();
            if (!text) {
                throw new Error('Empty response from API');
            }

            let result = JSON.parse(text);
            return result.credits;
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response.');
        }
    }

    async getTokenPrices(): Promise<TokenPrices> {
        try {
            const response = await fetch(this.url + 'v1/pricing', {
                method: 'GET',
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const text = await response.text();
            if (!text) {
                throw new Error('Empty response from API');
            }

            let result = JSON.parse(text);
            return result;
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response.');
        }
    }

    async createApiKey?(name : string) : Promise<IAPIKey>
    {
        const body = {
            name
        };

        return await this.post('v1/createapikey', body);
    }

    async deleteAPIKey(shortToken : string): Promise<void> {
        const response = await fetch(`${this.url}v1/apikeys/${shortToken}`, {
            method: 'DELETE',
            credentials: 'same-origin'
        });

        if (response.ok) {
            return;
        }

        throw new Error(`HTTP error! status: ${response.status}`);
    }

    async deleteChat(id : string): Promise<void> {
        try {
            const response = await fetch(`${this.url}v1/chats/${id}`, {
                method: 'DELETE',
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            await response.json();
            return;
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response from API');
        }
    }

    async deleteModel?(model: IModel): Promise<IDeleteModelResponse[]> {
        try {
            const response = await fetch(this.url + 'v1/models/' + model.id, {
                method: 'DELETE',
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response from chatbot');
        }
    }

    async forgotPassword?(email : string, altcha : string) : Promise<void>
    {
        const body = {
            email,
            altcha
        };

        return this.post('v1/forgotpassword', body);
    }

    async getReply(model: IModel, messages: Message[]): Promise<Message> {
        const apiMessages = messages.map(message => ({
            role: message.role,
            content: message.content
        }));

        try {
            const response = await fetch(this.url + 'v1/generate_response', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    model: model.name,
                    stream: false,
                    messages: apiMessages,
                    temperature: model.temperature,
                    top_p: model.topP
                })
            });

            const data = await response.json();
            if (!response.ok) {
                throw new ChatError("Error!: " + data.description);
            }

            return new Message(Role.Assistant, data.choices[0].message.content);
        }
        catch (error) {
            console.error('Error sending message to API:', error);

            if (error instanceof ChatError) {
                throw error;
            }

            throw new Error('Failed to get response from chatbot');
        }
    }

    async getReplyStreaming(model: IModel, messages: Message[], onPart: (part: string) => void): Promise<Message> {
        const apiMessages = messages.map(message => ({
            role: message.role,
            content: message.content
        }));

        try {
            const response = await fetch(this.url + 'v1/generate_response', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    model: model.name,
                    stream: true,
                    messages: apiMessages,
                    temperature: model.temperature,
                    top_p: model.topP
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new ChatError("Error!: " + data.description);
            }

            let fullContent = await this.readStream(response.body!.getReader(), onPart);
            return new Message(Role.Assistant, fullContent);
        }
        catch (error) {
            console.error('Error sending message to API:', error);

            if (error instanceof ChatError) {
                throw error;
            }
            
            throw new Error('Failed to get response from chatbot');
        }
    }

    async getState() : Promise<IState>
    {
        return await this.get('v1/status');
    }

    async loadChat(chat: IChatInfo): Promise<IChat> {
        try {
            const response = await fetch(this.url + 'v1/chats/' + chat.id, {
                method: 'GET',
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const text = await response.text();
            if (!text) {
                throw new Error('Empty response from API');
            }

            return JSON.parse(text);
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response from chatbot');
        }
    }

    async login?(email : string, password : string, altcha : string) : Promise<void>
    {
        const body = {
            email    : email,
            password : password,
            altcha   : altcha
        };

        await this.post('v1/login', body);
    }

    async logout?() : Promise<void>
    {
        await this.get('v1/logout');
    }

    async list(): Promise<IChatInfo[]> {
        try {
            const response = await fetch(this.url + 'v1/chats', {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response from chatbot');
        }
    }

    async modelList(): Promise<IModel[]> {
        try {
            const response = await fetch(this.url + 'v1/models', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response from chatbot');
        }
    }

    async createModel?(model: INewModel): Promise<IModel> {
        try {
            const response = await fetch(this.url + 'v1/models', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin',
                body: JSON.stringify(model)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response from chatbot');
        }
    }

    async readJsonStream(reader: ReadableStreamDefaultReader, onNextJson: (part: any) => void) {
        const decoder = new TextDecoder('utf-8');
        let done = false;
        let left = ''

        while (!done) {
            const { value, done: streamDone } = await reader.read();
            done = streamDone;
            if (value) {
                const chunk = decoder.decode(value, { stream: true });
                left = left + chunk;

                while (true) {
                    let endOfChunk = left.indexOf('\n');
                    if (endOfChunk === -1) {
                        break;
                    }

                    let line = left.substring(0, endOfChunk);
                    left = left.substring(endOfChunk + 1);

                    try {
                        const data = JSON.parse(line);
                        onNextJson(data as object);
                    } catch (e) {
                        console.log(e)
                        console.log(line)
                    }
                }
            }
        }
    }

    async readStream(reader: ReadableStreamDefaultReader, onPart: (part: string) => void): Promise<string> {
        let fullContent = '';
        await this.readJsonStream(reader, (data: any) => {
            if (data.type === 'delta') {
                fullContent += data.delta;
                onPart(data.delta);
            }
        });

        return fullContent;
    }

    async save(chat: ISaveChat): Promise<string> {
        try {
            const response = await fetch(this.url + 'v1/chats', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin',
                body: JSON.stringify(chat)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const text = await response.text();
            let res = JSON.parse(text);

            return res.id;
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response from chatbot');
        }
    }

    async train(model: IModel, query: Message[], responses: Message[], settings: ITraining, onProcess? : (progress : ITrainingChunk) => void) : Promise<void> 
    {
        const apiMessages = query.map(message => ({
            role: message.role,
            content: message.content
        }));

        const apiResponses = responses.map(reply => ({
            reward: reply.rating,
            response: reply.content
        }));

        let body = {
            model: model.name,
            stream: onProcess !== undefined,
            messages: apiMessages,
            responses: apiResponses
        };
        Object.assign(body, settings);

        const response = await fetch(this.url + 'v1/train', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify(body)
        });

        if (response.ok) {
            if (onProcess === undefined) {
                await response.json();
            }
            else {
                await this.readJsonStream(response.body!.getReader(), onProcess);
            }
        }
        else if (response.status === 400) {
            const { code, description } = await response.json();

            throw new ApiError(response.status, code, description);
        }
        else {
            throw new Error(`${response.status} ${response.statusText || 'Unknown Status'}`);
        }
    }

    async verify?(email : string, password : string, emailCode : string, altcha : string) : Promise<void>
    {
        const response = await fetch(`${this.url}v1/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                email     : email,
                emailCode : emailCode,
                password  : password,
                altcha    : altcha
            })
        });

        if (response.ok) {
            return;
        }
        else if (response.status === 400) {
            const { code, description } = await response.json();

            throw new ApiError(response.status, code, description);
        }
        else {
            throw new Error(`${response.status} ${response.statusText || 'Unknown Status'}`);
        }
    }

    async getBilling(from: string, to: string): Promise<IBilling[]> {
        try {
            const getBillUrl = new URL('v1/billing', this.url);
            getBillUrl.searchParams.append('from', from);
            getBillUrl.searchParams.append('to', to);

            const response = await fetch(getBillUrl, {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            let result = await response.json();
            return result;
        }
        catch (error) {
            console.error('Error sending message to API:', error);
            throw new Error('Failed to get response from chatbot');
        }
    }
};

